import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const utilitiesModule: ChecklistModuleDefinition = {
  code: 'utilities',
  name: 'Utilities',
  description: 'Electric, gas, internet, and communications',
  categories: [
    {
      code: 'electric',
      name: 'Electric',
      items: [
        {
          code: 'electric_availability',
          title: 'Electric service availability',
          description: 'Check for grid electric availability',
          isGroupTask: false,
          dataFields: [
            { key: 'available', label: 'Available', type: 'boolean' },
            { key: 'connected', label: 'Currently Connected', type: 'boolean' },
            { key: 'provider', label: 'Provider', type: 'string' },
            { key: 'service_type', label: 'Service Type', type: 'select', options: [
              { value: 'single_phase', label: 'Single Phase' },
              { value: 'three_phase', label: 'Three Phase' },
              { value: 'unknown', label: 'Unknown' },
            ]},
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'No electric available' },
            { flag: 'green', condition: 'Electric service available' },
          ],
        },
        {
          code: 'electric_distance',
          title: 'Distance to electric',
          description: 'Distance from property to nearest service',
          isGroupTask: false,
          dataFields: [
            { key: 'distance_to_service', label: 'Distance to Service', type: 'number', unit: 'feet' },
            { key: 'connection_cost', label: 'Est. Connection Cost', type: 'number', unit: 'dollars' },
            { key: 'cost_per_foot', label: 'Cost Per Foot', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Expensive electric connection (>$10k)' },
            { flag: 'yellow', condition: 'Moderate connection cost ($2-10k)' },
          ],
        },
        {
          code: 'electric_capacity',
          title: 'Electric capacity assessment',
          description: 'Evaluate if service meets needs',
          isGroupTask: false,
          dataFields: [
            { key: 'current_service_amps', label: 'Current Service Amps', type: 'number', unit: 'amps' },
            { key: 'adequate_for_needs', label: 'Adequate for Needs', type: 'boolean' },
            { key: 'upgrade_needed', label: 'Upgrade Needed', type: 'boolean' },
            { key: 'upgrade_cost', label: 'Upgrade Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'solar_potential',
          title: 'Solar potential',
          description: 'Assess solar energy viability',
          isGroupTask: false,
          dataFields: [
            { key: 'good_solar_exposure', label: 'Good Solar Exposure', type: 'boolean' },
            { key: 'existing_solar', label: 'Existing Solar', type: 'boolean' },
            { key: 'solar_notes', label: 'Notes', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Excellent solar potential' },
          ],
        },
      ],
    },
    {
      code: 'gas',
      name: 'Natural Gas/Propane',
      items: [
        {
          code: 'natural_gas',
          title: 'Natural gas availability',
          description: 'Check for natural gas service',
          isGroupTask: false,
          dataFields: [
            { key: 'available', label: 'Available', type: 'boolean' },
            { key: 'connected', label: 'Connected', type: 'boolean' },
            { key: 'provider', label: 'Provider', type: 'string' },
          ],
        },
        {
          code: 'propane_assessment',
          title: 'Propane setup',
          description: 'Existing or needed propane infrastructure',
          isGroupTask: false,
          dataFields: [
            { key: 'has_propane', label: 'Has Propane Tank', type: 'boolean' },
            { key: 'tank_size', label: 'Tank Size', type: 'number', unit: 'gallons' },
            { key: 'tank_owned', label: 'Tank Owned (vs Leased)', type: 'boolean' },
            { key: 'tank_condition', label: 'Tank Condition', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'internet_communications',
      name: 'Internet & Communications',
      items: [
        {
          code: 'internet_options',
          title: 'Internet service options',
          description: 'Research available internet providers/types',
          isGroupTask: false,
          dataFields: [
            { key: 'available_types', label: 'Available Types', type: 'multiselect', options: [
              { value: 'fiber', label: 'Fiber' },
              { value: 'cable', label: 'Cable' },
              { value: 'dsl', label: 'DSL' },
              { value: 'fixed_wireless', label: 'Fixed Wireless' },
              { value: 'satellite', label: 'Satellite' },
              { value: 'cellular', label: 'Cellular/LTE/5G' },
              { value: 'starlink', label: 'Starlink' },
              { value: 'none', label: 'None Available' },
            ]},
            { key: 'best_option', label: 'Best Option', type: 'string' },
            { key: 'max_speed', label: 'Max Speed Available', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'No reliable internet available' },
            { flag: 'red', condition: 'Only satellite/slow options' },
            { flag: 'green', condition: 'High-speed internet available' },
            { flag: 'big_green', condition: 'Fiber internet available' },
          ],
        },
        {
          code: 'cell_coverage',
          title: 'Cell phone coverage',
          description: 'Test/research cellular signal at property',
          isGroupTask: false,
          dataFields: [
            { key: 'carriers_tested', label: 'Carriers Tested', type: 'array' },
            { key: 'signal_quality', label: 'Signal Quality', type: 'select', options: [
              { value: 'excellent', label: 'Excellent (5 bars)' },
              { value: 'good', label: 'Good (3-4 bars)' },
              { value: 'fair', label: 'Fair (2 bars)' },
              { value: 'poor', label: 'Poor (1 bar)' },
              { value: 'none', label: 'No Signal' },
            ]},
            { key: 'best_carrier', label: 'Best Carrier', type: 'string' },
            { key: 'signal_booster_needed', label: 'Signal Booster Needed', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'No cell coverage' },
            { flag: 'yellow', condition: 'Poor cell coverage' },
          ],
        },
        {
          code: 'landline',
          title: 'Landline availability',
          description: 'Traditional phone service availability',
          isGroupTask: false,
          dataFields: [
            { key: 'available', label: 'Available', type: 'boolean' },
            { key: 'connected', label: 'Connected', type: 'boolean' },
          ],
        },
      ],
    },
    {
      code: 'other_utilities',
      name: 'Other Utilities',
      items: [
        {
          code: 'trash_service',
          title: 'Trash/waste collection',
          description: 'Garbage pickup availability',
          isGroupTask: false,
          dataFields: [
            { key: 'available', label: 'Available', type: 'boolean' },
            { key: 'provider', label: 'Provider', type: 'string' },
            { key: 'monthly_cost', label: 'Monthly Cost', type: 'number', unit: 'dollars' },
            { key: 'alternative_options', label: 'Alternative Options', type: 'string' },
          ],
        },
        {
          code: 'cable_tv',
          title: 'Cable TV availability',
          description: 'Cable television service availability',
          isGroupTask: false,
          dataFields: [
            { key: 'available', label: 'Available', type: 'boolean' },
            { key: 'providers', label: 'Providers', type: 'array' },
          ],
        },
        {
          code: 'emergency_services',
          title: 'Emergency services',
          description: 'Fire, EMS, police response',
          isGroupTask: false,
          dataFields: [
            { key: 'fire_department', label: 'Fire Department', type: 'string' },
            { key: 'fire_response_time', label: 'Fire Response Time', type: 'string' },
            { key: 'ems_provider', label: 'EMS Provider', type: 'string' },
            { key: 'police_jurisdiction', label: 'Police Jurisdiction', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Long emergency response times' },
          ],
        },
      ],
    },
  ],
};

